import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon,
  UsersIcon,
  ClockIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  BanknotesIcon,
  UserGroupIcon,
  CubeIcon,
  TrophyIcon,
  BriefcaseIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  BellIcon,
  CurrencyDollarIcon,
  ReceiptRefundIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  GiftIcon,
  EnvelopeIcon,
  UserCircleIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { USER_ROLES, hasAnyRole } from '../../utils/rbac';

const Sidebar = ({ sidebarOpen, setSidebarOpen, currentPath, userRole, subscription, subscriptionLoading, subscriptionError }) => {
  const { t } = useTranslation(['navigation', 'common']);

  // Default to employee role if no role is provided
  const currentUserRole = userRole || 'employee';

  // Normalize subscription shape: support { success, data } or plain subscription
  const sub = subscription ? (subscription.data ? subscription.data : subscription) : null;

  // Check if loan module is accessible
  const isLoanAccessible = () => {
    // Super admin always has access
    if (currentUserRole === 'super_admin') return true;

    // If subscription is loading or there's an error, don't show the module
    if (subscriptionLoading || subscriptionError || !sub) return false;

    // Check if trial is active (if trial expired, don't allow)
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes loan module (DB contains 'loans' in modules)
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('loans') || false;
  };

  // Check if asset module is accessible
  const isAssetAccessible = () => {
    // Super admin always has access
    if (currentUserRole === 'super_admin') return true;

    // If subscription is loading or there's an error, don't show the module
    if (subscriptionLoading || subscriptionError || !sub) return false;

    // Check if trial is active (if trial expired, don't allow)
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes assets module (DB contains 'assets' in modules)
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('assets') || false;
  };

  // Check if letters module is accessible
  const isLettersAccessible = () => {
    // Super admin always has access
    if (currentUserRole === 'super_admin') return true;

    // If subscription is loading or there's an error, don't show the module
    if (subscriptionLoading || subscriptionError || !sub) return false;

    // Check if trial is active (if trial expired, don't allow)
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes letters module (DB contains 'letters' in modules)
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('letters') || false;
  };

  // Define navigation items with role-based and plan-based access
  const allNavigationItems = [
    {
      name: t('dashboard'),
      href: '/dashboard',
      icon: HomeIcon,
      current: currentPath === '/dashboard',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: ChartBarIcon,
      current: currentPath === '/analytics',
      roles: [USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    // Profile is accessible from header user menu; do not show in sidebar
    {
      name: t('employees'),
      href: '/employees',
      icon: UsersIcon,
      current: currentPath === '/employees',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: t('master'),
      href: '/master',
      icon: BuildingOfficeIcon,
      current: currentPath === '/master',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: 'Tax Master',
      href: '/tax-master',
      icon: DocumentTextIcon,
      current: currentPath === '/tax-master',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN],
    },
    {
      name: 'Salary Structure',
      href: '/salary-structure',
      icon: CurrencyDollarIcon,
      current: currentPath === '/salary-structure',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: 'Payroll',
      href: '/payroll',
      icon: ReceiptRefundIcon,
      current: currentPath === '/payroll',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: t('attendance'),
      href: '/attendance',
      icon: ClockIcon,
      current: currentPath === '/attendance',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
    },
    {
      name: 'Task Management',
      href: '/tasks',
      icon: ClipboardDocumentListIcon,
      current: currentPath === '/tasks',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
    },
    {
      name: t('leave'),
      href: '/leave',
      icon: CalendarDaysIcon,
      current: currentPath === '/leave',
      roles: [USER_ROLES.EMPLOYEE],
    },
    {
      name: 'Leave Management',
      href: '/admin-leave',
      icon: CalendarDaysIcon,
      current: currentPath === '/admin-leave',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: t('loan'),
      href: '/loan',
      icon: BanknotesIcon,
      current: currentPath === '/loan',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
      planCheck: isLoanAccessible,
    },
    {
      name: 'Assets',
      href: '/assets',
      icon: CubeIcon,
      current: currentPath === '/assets',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
      planCheck: isAssetAccessible,
    },
    {
      name: 'Letters',
      href: '/letters',
      icon: DocumentTextIcon,
      current: currentPath === '/letters',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
      planCheck: isLettersAccessible,
    },
    {
      name: 'Salary Slips',
      href: '/salary-slips',
      icon: DocumentTextIcon,
      current: currentPath === '/salary-slips',
      roles: [USER_ROLES.EMPLOYEE],
    },
    {
      name: 'Holiday Management',
      href: '/holidays',
      icon: GiftIcon,
      current: currentPath === '/holidays',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    // {
    //   name: t('performance'),
    //   href: '/performance',
    //   icon: TrophyIcon,
    //   current: currentPath === '/performance',
    //   roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    // },
    // {
    //   name: t('recruitment'),
    //   href: '/recruitment',
    //   icon: BriefcaseIcon,
    //   current: currentPath === '/recruitment',
    //   roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    // },
    {
      name: t('reports'),
      href: '/reports',
      icon: DocumentChartBarIcon,
      current: currentPath === '/reports',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
    {
      name: 'TDS Reports',
      href: '/tds-reports',
      icon: DocumentTextIcon,
      current: currentPath === '/tds-reports',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN],
    },
   
    // {
    //   name: 'Branch Settings',
    //   href: '/branch-settings',
    //   icon: BuildingOfficeIcon,
    //   current: currentPath === '/branch-settings',
    //   roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN],
    // },
    // Super Admin Only Navigation
    {
      name: 'Super Admin',
      href: '/super-admin/dashboard',
      icon: ShieldCheckIcon,
      current: currentPath.startsWith('/super-admin'),
      roles: [USER_ROLES.SUPER_ADMIN],
    },
    {
      name: 'Companies',
      href: '/super-admin/companies',
      icon: BuildingOfficeIcon,
      current: currentPath === '/super-admin/companies',
      roles: [USER_ROLES.SUPER_ADMIN],
    },
    {
      name: 'Plans',
      href: '/super-admin/plans',
      icon: CubeIcon,
      current: currentPath === '/super-admin/plans',
      roles: [USER_ROLES.SUPER_ADMIN],
    },
    {
      name: 'Payments',
      href: '/super-admin/payments',
      icon: CreditCardIcon,
      current: currentPath === '/super-admin/payments',
      roles: [USER_ROLES.SUPER_ADMIN],
    },
    {
      name: 'Data Cleanup',
      href: '/super-admin/data-cleanup',
      icon: Cog6ToothIcon,
      current: currentPath === '/super-admin/data-cleanup',
      roles: [USER_ROLES.SUPER_ADMIN],
    },
    {
      name: 'Inquiries',
      href: '/super-admin/inquiries',
      icon: EnvelopeIcon,
      current: currentPath === '/super-admin/inquiries',
      roles: [USER_ROLES.SUPER_ADMIN],
    },
     {
      name: 'Notifications',
      href: '/notifications',
      icon: BellIcon,
      current: currentPath === '/notifications',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN, USER_ROLES.EMPLOYEE],
    },
     {
      name: t('settings'),
      href: '/settings',
      icon: Cog6ToothIcon,
      current: currentPath === '/settings',
      roles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN],
    },
  ];

  // Filter navigation items based on user role and plan access
  // If user is super admin, show only super admin exclusive menu items
  const navigationItems = currentUserRole === USER_ROLES.SUPER_ADMIN 
    ? allNavigationItems.filter(item => item.roles.includes(USER_ROLES.SUPER_ADMIN) && item.roles.length === 1)
    : allNavigationItems.filter(item => 
        hasAnyRole(currentUserRole, item.roles) && 
        !(item.roles.length === 1 && item.roles[0] === USER_ROLES.SUPER_ADMIN) &&
        (!item.planCheck || item.planCheck())
      );

  const companyInfo = {
    name: 'Innovative HRMS',
    logo: null, // In real app, use company logo
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col lg:z-40 transition-all duration-500 ease-in-out ${
        sidebarOpen ? 'lg:w-72' : 'lg:w-20'
      }`}>
        <div className="flex flex-col flex-grow bg-white border-r border-secondary-200 shadow-xl overflow-y-auto">
          {/* Logo Section */}
          <div className="flex items-center justify-center flex-shrink-0 px-4 py-2">
            <div className="relative">
              {/* Logo Image */}
              <img
                src="/assets/logo.png"
                alt="Innovative HRMS Logo"
                className="h-24 w-auto object-contain"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden h-20 w-20 bg-gradient-primary rounded-xl items-center justify-center shadow-glow">
                <span className="text-white font-bold text-base">HR</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-secondary-300 scrollbar-track-transparent hover:scrollbar-thumb-secondary-400">
            {navigationItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `group flex items-center px-4 py-4 text-sm font-medium rounded-2xl transition-all duration-300 ease-in-out transform hover:scale-105 ${
                      isActive
                        ? 'bg-gradient-primary text-white shadow-glow ring-1 ring-primary-200'
                        : 'text-secondary-700 hover:text-primary-600 hover:bg-primary-50/80 hover:shadow-soft'
                    } ${!sidebarOpen ? 'justify-center' : ''}`
                  }
                  title={!sidebarOpen ? item.name : ''}
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  {({ isActive }) => (
                    <>
                      <div className={`flex-shrink-0 h-6 w-6 transition-all duration-300 ${
                        isActive ? 'text-white' : 'text-secondary-500 group-hover:text-primary-600'
                      }`}>
                        <IconComponent />
                      </div>
                      {sidebarOpen && (
                        <span className="ml-4 transition-all duration-300 font-medium">{item.name}</span>
                      )}
                      {sidebarOpen && isActive && (
                        <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User Info at Bottom */}
          {sidebarOpen && (
            <div className="flex-shrink-0 border-t border-secondary-100 p-6 animate-fade-in-up">
              <div className="flex items-center space-x-4">
                <div className="h-12 w-12 bg-gradient-secondary rounded-2xl flex items-center justify-center shadow-soft">
                  <UserGroupIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-secondary-900 truncate">Company Portal</p>
                  <p className="text-xs text-secondary-500">v2.1.0</p>
                  <div className="flex items-center mt-2">
                    <div className="flex-1 bg-secondary-200 rounded-full h-1.5">
                      <div className="bg-gradient-primary h-1.5 rounded-full w-3/4 animate-pulse"></div>
                    </div>
                    <span className="text-xs text-secondary-500 ml-2">75%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>      {/* Mobile Sidebar Backdrop */}
      <div className={`lg:hidden fixed inset-0 bg-secondary-900/70 z-40 transition-opacity duration-300 ${
        sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`} onClick={() => setSidebarOpen(false)} />

      {/* Mobile Sidebar Content */}
      <div className={`lg:hidden fixed inset-y-0 left-0 z-50 flex flex-col max-w-xs w-full bg-white transform transition-transform duration-300 shadow-2xl ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
          {/* Close Button */}
          <div className="absolute top-4 right-4 z-60">
            <button
              type="button"
              className="flex items-center justify-center h-10 w-10 rounded-xl bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all duration-200 hover:scale-105 border border-secondary-200"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="h-5 w-5 text-secondary-600" />
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="flex items-center justify-center flex-shrink-0 px-4 py-2 border-b border-secondary-100">
            <div className="relative">
              {/* Logo Image */}
              <img
                src="/assets/logo.png"
                alt="Innovative HRMS Logo"
                className="h-20 w-auto object-contain"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden h-20 w-20 bg-gradient-primary rounded-xl items-center justify-center shadow-glow">
                <span className="text-white font-bold text-base">HR</span>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="mt-6 flex-1 h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-secondary-300 scrollbar-track-transparent hover:scrollbar-thumb-secondary-400">
            <nav className="px-4 space-y-2">
              {navigationItems.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center px-4 py-4 text-sm font-medium rounded-2xl transition-all duration-300 ease-in-out transform hover:scale-105 ${
                        isActive
                          ? 'bg-gradient-primary text-white shadow-glow'
                          : 'text-secondary-700 hover:text-primary-600 hover:bg-primary-50/80 hover:shadow-soft'
                      }`
                    }
                    onClick={() => setSidebarOpen(false)}
                    style={{animationDelay: `${index * 0.1}s`}}
                  >
                    {({ isActive }) => (
                      <>
                        <div className={`flex-shrink-0 h-6 w-6 transition-all duration-300 ${
                          isActive ? 'text-white' : 'text-secondary-500 group-hover:text-primary-600'
                        }`}>
                          <IconComponent />
                        </div>
                        <span className="ml-4 font-medium">{item.name}</span>
                        {isActive && (
                          <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          {/* Mobile User Info */}
          <div className="flex-shrink-0 border-t border-secondary-100 p-6">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gradient-secondary rounded-2xl flex items-center justify-center shadow-soft">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-secondary-900">Company Portal</p>
                <p className="text-xs text-secondary-500">v2.1.0</p>
                <div className="flex items-center mt-2">
                  <div className="flex-1 bg-secondary-200 rounded-full h-1.5">
                    <div className="bg-gradient-primary h-1.5 rounded-full w-3/4"></div>
                  </div>
                  <span className="text-xs text-secondary-500 ml-2">75%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );
};

export default Sidebar;
